"use client"

import React, { useState, useEffect, useRef } from "react"
import { motion } from "framer-motion"
import { ConsentBanner, ConsentDeclined } from "./ConsentBanner"
import { DemoExplorer } from "./DemoExplorer"
import { DemoChat } from "./DemoChat"
import { Card } from "@/components/ui/card"
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { useIsMobile } from "@/hooks/use-mobile"
import { Button } from "@/components/ui/button"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { MessageSquare, PlayCircle } from "lucide-react"
import { useDemoStore } from "@/store/demoStore"
import { UpgradeModal } from "./UpgradeModal"
const CONSENT_KEY = "demo-consent-given"

const DemoSection: React.FC = () => {
  const [hasConsent, setHasConsent] = useState<boolean | null>(null)
  const [hasDeclined, setHasDeclined] = useState(false)
  const isMobile = useIsMobile()
  // On desktop, chat is open by default. On mobile, it starts closed and opens in a sheet.
  const [isChatOpen, setIsChatOpen] = useState(!isMobile)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  // Track the real visible area using visualViewport API.
  // On iOS Safari with the bottom URL bar, position:fixed bottom:0 anchors to the
  // layout viewport bottom (below the URL bar), not the visual viewport bottom.
  // We must use top:offsetTop + height to cover exactly the visible area.
  const [sheetHeight, setSheetHeight] = useState<number | null>(null)
  const [sheetOffsetTop, setSheetOffsetTop] = useState<number>(0)

  // Load explorer when component mounts (after consent check)
  const [shouldLoadExplorer, setShouldLoadExplorer] = useState(false)
  const sectionRef = useRef<HTMLDivElement>(null)

  // Get limit state from store
  const limitReached = useDemoStore((state) => state.limitReached)

  // Update chat open state when mobile state changes
  useEffect(() => {
    if (!isMobile) {
      setIsChatOpen(true) // Desktop: always show chat
    } else {
      setIsChatOpen(false) // Mobile: start closed
    }
  }, [isMobile])

  // Close mobile chat when limit is reached
  useEffect(() => {
    if (isMobile && limitReached) {
      setIsChatOpen(false)
    }
  }, [isMobile, limitReached])

  // Hide site header when mobile sheet is open so fixed+transformed nav elements
  // (TopBanner, Navbar use framer-motion GPU layers) don't bleed above the sheet
  useEffect(() => {
    const header = document.getElementById('site-header')
    if (!header) return
    if (isMobile && isChatOpen) {
      header.style.display = 'none'
    } else {
      header.style.display = ''
    }
    return () => {
      header.style.display = ''
    }
  }, [isMobile, isChatOpen])

  // Use visualViewport API to get the true rendered area on iOS Safari.
  // CSS units (dvh/svh/lvh) are all unreliable because position:fixed bottom:0
  // anchors to the layout viewport bottom (below the URL bar), not the visual
  // viewport bottom. We use top:offsetTop + height:vv.height to cover exactly
  // the visible area regardless of whether the URL bar is top or bottom.
  useEffect(() => {
    if (!isMobile) return

    const update = () => {
      const vv = window.visualViewport
      setSheetHeight(vv?.height ?? window.innerHeight)
      setSheetOffsetTop(vv?.offsetTop ?? 0)
    }

    update()
    window.visualViewport?.addEventListener('resize', update)
    window.visualViewport?.addEventListener('scroll', update)

    return () => {
      window.visualViewport?.removeEventListener('resize', update)
      window.visualViewport?.removeEventListener('scroll', update)
    }
  }, [isMobile])

  // Track previous limitReached state to detect transitions
  const prevLimitReachedRef = React.useRef(limitReached)

  // Show upgrade modal when limit is reached
  useEffect(() => {
    // Only show modal when limitReached transitions from false to true
    if (limitReached && !prevLimitReachedRef.current) {
      setShowUpgradeModal(true)
    }
    prevLimitReachedRef.current = limitReached
  }, [limitReached])

  // Load explorer immediately when component is ready
  // DemoSection is already lazy loaded at the page level, so we can load the explorer right away
  useEffect(() => {
    if (hasConsent !== null) {
      // Small delay to allow DOM to settle after consent is determined
      const timer = setTimeout(() => {
        setShouldLoadExplorer(true)
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [hasConsent])

  useEffect(() => {
    // Check localStorage for existing consent
    if (typeof window !== "undefined") {
      const consent = localStorage.getItem(CONSENT_KEY)
      if (consent === "true") {
        setHasConsent(true)
      } else if (consent === "false") {
        setHasDeclined(true)
        setHasConsent(false)
      } else {
        setHasConsent(false)
      }
    }
  }, [])


  const handleAccept = () => {
    if (typeof window !== "undefined") {
      localStorage.setItem(CONSENT_KEY, "true")
      setHasConsent(true)
      setHasDeclined(false)
    }
  }

  const handleDecline = () => {
    if (typeof window !== "undefined") {
      localStorage.setItem(CONSENT_KEY, "false")
      setHasConsent(false)
      setHasDeclined(true)
    }
  }

  const handleSignup = () => {
    // Redirect to login page
    window.location.href = "https://app.infinitcode.ai/login"
  }

  if (hasConsent === null) {
    // Loading state
    return null
  }

  // Always show demo components, with overlay when needed
  return (
    <section id="interactive-demo" className="relative py-12 !bg-white" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="absolute left-1/2 top-0 -translate-x-1/2 translate-y-[calc(-100%+1px)] md:w-[25%]">
            <div className="relative bg-white max-md:px-8 px-4 py-3 rounded-t-3xl w-full">
              <h2 className="text-xl md:text-3xl font-bold text-gray-900 flex flex-nowrap justify-center gap-[6px] whitespace-nowrap w-full">
                See It in
                <span className="bg-gradient-to-r from-[#3BCF8E] to-[#2DA771] text-transparent bg-clip-text">
                  Action
                </span>
              </h2>
              {/* Left corner cutout - square with quarter-circle cutout (pointy edge at bottom-right) */}
              <svg
                className="absolute bottom-0 -left-[23px] w-[24px] h-[24px] pointer-events-none"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  <mask id="left-corner-mask">
                    <rect width="24" height="24" fill="white" />
                    <circle cx="0" cy="0" r="23" fill="black" />
                  </mask>
                </defs>
                <rect width="24" height="24" fill="white" mask="url(#left-corner-mask)" />
              </svg>
              {/* Right corner cutout - square with quarter-circle cutout (pointy edge at bottom-left) */}
              <svg
                className="absolute bottom-0 -right-[23px] w-[24px] h-[24px] pointer-events-none"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  <mask id="right-corner-mask">
                    <rect width="24" height="24" fill="white" />
                    <circle cx="24" cy="0" r="23" fill="black" />
                  </mask>
                </defs>
                <rect width="24" height="24" fill="white" mask="url(#right-corner-mask)" />
              </svg>
            </div>
          </div>
          <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
            Discover how our AI helps you understand and improve your code in real-time. Ask questions, or explore your codebase—all through a powerful integration that&apos;s changing how teams work with code.
          </p>
        </div>

        <div className="relative">
          <Card className="w-full h-[750px] !overflow-hidden" data-demo-card>
            <ResizablePanelGroup direction="horizontal" className="h-full min-h-0 overflow-hidden">
              {/* Chat panel - left (desktop only when open) */}
              {isChatOpen && !isMobile && (
                <>
                  <ResizablePanel
                    id="demo-chat-panel"
                    defaultSize={40}
                    minSize={30}
                    className="min-w-[300px] relative min-h-0"
                  >
                    <div className="h-full" data-demo-chat>
                      <DemoChat onSignup={handleSignup} disableUpgradeModal={true} />
                    </div>
                  </ResizablePanel>
                  <ResizableHandle withHandle />
                </>
              )}
              {/* React Flow panel - right */}
              <ResizablePanel
                id="demo-react-flow"
                defaultSize={isChatOpen && !isMobile ? 60 : 100}
                minSize={isChatOpen && !isMobile ? 30 : 100}
                className="relative min-h-0"
              >
                <div className="h-full" data-demo-explorer>
                  {shouldLoadExplorer ? (
                    <DemoExplorer />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center bg-gray-50">
                      <div className="text-gray-600">Loading explorer...</div>
                    </div>
                  )}
                </div>
                {/* Mobile chat button - floating button to open chat */}
                {isMobile && (
                  <div className="absolute top-5 right-5 z-50">
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="animate-glow animate-glow-overlay hover:[animation:none]">
                            <Button
                              onClick={() => setIsChatOpen(true)}
                              className="rounded-lg h-10 w-10 p-0 aspect-square relative"
                            >
                              <MessageSquare className="!h-6 !w-6" />
                            </Button>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent 
                          side="top"
                          className="bg-primary text-primary-foreground border-primary/20 rounded-xl px-3 py-1.5 text-sm font-medium shadow-lg"
                        >
                          <p>Chat</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                )}
              </ResizablePanel>
            </ResizablePanelGroup>
          </Card>

          {/* Mobile: Bottom sheet chat overlay */}
          {isMobile && (
            <Sheet open={isChatOpen} onOpenChange={setIsChatOpen}>
              <SheetContent
                side="bottom"
                className="!p-1 flex flex-col !gap-0"
                style={sheetHeight !== null ? {
                  top: sheetOffsetTop,
                  bottom: 'auto',
                  height: sheetHeight,
                  maxHeight: sheetHeight,
                } : undefined}
              >
                <SheetHeader className="px-4 pt-3 pb-1">
                  <SheetTitle>Infinitcode AI</SheetTitle>
                </SheetHeader>
                <div className="flex-1 min-h-0 flex flex-col">
                  <DemoChat onSignup={handleSignup} hideTitle={true} disableUpgradeModal={true} />
                </div>
              </SheetContent>
            </Sheet>
          )}

          {/* Overlay when no consent */}
          {!hasConsent && !hasDeclined && (
            <>
              {/* Blurred backdrop */}
              <div className="absolute inset-0 bg-background/60 backdrop-blur-sm z-40 rounded-lg" />
              {/* Consent banner overlay */}
              <div className="absolute bottom-4 right-4 left-4 lg:left-auto z-50 max-w-md lg:max-w-md mx-auto lg:mx-0">
                <ConsentBanner onAccept={handleAccept} onDecline={handleDecline} />
              </div>
            </>
          )}

          {/* Overlay when declined */}
          {hasDeclined && (
            <>
              {/* Blurred backdrop */}
              <div className="absolute inset-0 bg-background/60 backdrop-blur-sm z-40 rounded-lg" />
              {/* Consent declined overlay */}
              <div className="absolute bottom-4 right-4 left-4 lg:left-auto z-50 max-w-md lg:max-w-md mx-auto lg:mx-0">
                <ConsentDeclined onSignup={handleSignup} onAccept={handleAccept} />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Upgrade modal - rendered at section level to persist when sheet closes */}
      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        onSignup={handleSignup}
      />
    </section>
  )
}

export default DemoSection

